import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import L, { LatLngTuple } from 'leaflet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from 'react-router';

import { AddLocationFormValues, addLocationSchema } from 'api/InteractiveMap/types';
import { MapLocation } from 'api/traps/types';
import { MLInput } from 'components/Input/Input';
import usePostLocationOnMap from 'hooks/InteractiveMap/usePostLocationOnMap';
import { deleteLocationOnMap } from 'api/InteractiveMap/deleteLocationOnMap';
import { putLocationOnMap } from 'api/InteractiveMap/putLocationOnMap';
import { truncateText } from 'utils/truncateText';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}
interface MapSettingsProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  setColorPolygon: (color: string) => void;
  onToggleDrawMode: (isActive: boolean) => void;
  onConfirm: (formData: { title: string; number: string; coordinates: [number, number][]; color: string }) => void;
  location?: MapLocation[];
  locationSet?: (locations: MapLocation[]) => void;
  setEditingIndex: (index: number | null) => void;
  planSlug?: string;
  tempPolygon?: LatLngTuple[];
  refetchLocations?: () => void;
}

const LocationSettings: React.FC<MapSettingsProps> = ({
  isOpen,
  setIsOpen,
  setColorPolygon,
  onToggleDrawMode,
  onConfirm,
  location = [],
  locationSet,
  setEditingIndex,
  planSlug,
  tempPolygon = [],
  refetchLocations,
}) => {
  const query = useQuery();
  const openLocationModal = query.get('openLocationModal') === 'true';
  const [showAddLocationPopup, setShowAddLocationPopup] = useState(false);
  const [polygonColor, setPolygonColor] = useState<string>('#800080');
  const [currentEditingIndex, setCurrentEditingIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { createNewLocation } = usePostLocationOnMap();
  const colorDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();
  const {
    control: siteControl,
    reset,
    handleSubmit,
    setValue,
  } = useForm<AddLocationFormValues>({
    resolver: zodResolver(addLocationSchema),
    defaultValues: {
      title: '',
      number: undefined,
      coordinates: [],
      color: '#800080',
    },
  });

  useEffect(() => {
    if (dropdownRef.current) {
      L.DomEvent.disableClickPropagation(dropdownRef.current);
      L.DomEvent.disableScrollPropagation(dropdownRef.current);
    }
  }, [dropdownRef]);
  useEffect(() => {
    if (openLocationModal) {
      setIsOpen(true);

      const url = new URL(window.location.href);
      url.searchParams.delete('openLocationModal');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openLocationModal]);
  useEffect(() => {
    return () => {
      if (colorDebounceRef.current) {
        clearTimeout(colorDebounceRef.current);
      }
    };
  }, []);

  const deleteLocation = async (indexToDelete: number) => {
    try {
      const locId = location[indexToDelete].id;

      if (!locId) {
        const updatedLocations = location.filter((_, index) => index !== indexToDelete);
        locationSet?.(updatedLocations);
        return;
      }

      await deleteLocationOnMap(planSlug!, locId);

      const updatedLocations = location.filter((_, index) => index !== indexToDelete);
      locationSet?.(updatedLocations);

      refetchLocations?.();
      enqueueSnackbar(t('LocationSettings.deleteLocation'), { variant: 'success' });
    } catch (error) {
      console.error('Failed to delete location', error);
      enqueueSnackbar(t('LocationSettings.deleteLocationError'), { variant: 'error' });
    }
  };
  const editLocation = (indexToEdit: number) => {
    const loc = location[indexToEdit];

    reset({
      title: loc.title,
      number: loc.number,
      coordinates: [],
      color: loc.color,
    });

    setPolygonColor(loc.color);
    setColorPolygon(loc.color);

    if (locationSet) {
      const updatedLocations = [...location];
      updatedLocations[indexToEdit] = {
        ...updatedLocations[indexToEdit],
        coordinates: [],
      };
      locationSet(updatedLocations);
    }

    setCurrentEditingIndex(indexToEdit);
    setEditingIndex(indexToEdit);

    onToggleDrawMode(true);
    setShowAddLocationPopup(true);
  };

  const getStrictCoordinates = (): [number, number][] => {
    return tempPolygon.map(point => [point[0], point[1]] as [number, number]);
  };

  const onSubmitForm = async (data: AddLocationFormValues) => {
    if (!planSlug) return;

    if (currentEditingIndex !== null) {
      const locationId = location[currentEditingIndex].id;

      if (locationId) {
        try {
          const res = await putLocationOnMap(planSlug, locationId, {
            title: data.title,
            number: data.number,
            coordinates: data.coordinates,
            color: data.color,
          });
          if (res) {
            enqueueSnackbar(t('LocationSettings.updateSuccess'), { variant: 'success' });
          }
        } catch (error) {
          console.error('Failed to update location', error);
        }
      }
    } else {
      createNewLocation(
        { plan: data, slug: planSlug },
        {
          onSuccess: () => {
            refetchLocations?.();
            enqueueSnackbar(t('LocationSettings.createSuccess'), { variant: 'success' });
          },
          onError: () => {
            enqueueSnackbar(t('LocationSettings.createError'), { variant: 'error' });
          },
        },
      );
    }

    onConfirm(data);
    setShowAddLocationPopup(false);
    setCurrentEditingIndex(null);
  };

  const onFormError = (errors: any) => {
    if (errors.coordinates) {
      enqueueSnackbar(errors.coordinates.message || t('LocationSettings.drawPolygonRequired'), { variant: 'error' });
    }
  };
  return (
    <div
      ref={dropdownRef}
      title="Map Settings"
      onMouseDown={e => e.stopPropagation()}
      onMouseUp={e => e.stopPropagation()}
      onMouseMove={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
      onPointerMove={e => e.stopPropagation()}
      onPointerUp={e => e.stopPropagation()}
      onTouchStart={e => e.stopPropagation()}
      onTouchMove={e => e.stopPropagation()}
      onTouchEnd={e => e.stopPropagation()}
      onDoubleClick={e => e.stopPropagation()}
      style={{
        position: 'absolute',
        top: '26px',
        right: '110px',
        zIndex: 1000,

        fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
      }}
    >
      <button
        className="map-dropdown-toggle"
        title={t('LocationSettings.mapLocation')}
        onClick={() => setIsOpen(!isOpen)}
        style={{ position: 'relative' }}
      >
        {t('LocationSettings.location')} ▾
      </button>

      {isOpen && (
        <div
          className="map-popup-content"
          style={{
            position: 'absolute',
            top: '100%',
            right: '-90px',
            width: '290px',
            height: '250px',
            overflow: 'hidden',
            padding: 0,
            zIndex: 9999,
            marginTop: '4px',
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          }}
        >
          <div className="py-2">
            <div className="border-bottom mb-2 w-100">
              <div className="d-flex justify-content-between align-items-center border-1 pb-2 px-2">
                <div style={{ fontWeight: 700, fontSize: 18, color: '#3a3a6a' }}>
                  {t('LocationSettings.mapLocation')}
                </div>
                <button
                  className="btn btn-light waves-effect btn-sm fs-5 fw-semibold"
                  style={{ borderRadius: '50%', width: '32px', height: '32px', padding: 0 }}
                  onClick={() => setIsOpen(false)}
                >
                  <i className="ri-close-line" />
                </button>
              </div>
            </div>
            <div className="px-2">
              <div className="text-center">
                <Button
                  className="btn btn-ghost-success"
                  onClick={() => {
                    reset({ title: '', number: '', coordinates: [], color: '#800080' });
                    setEditingIndex(null);
                    setCurrentEditingIndex(null);
                    setShowAddLocationPopup(true);
                    onToggleDrawMode(true);
                  }}
                  title={t('LocationSettings.addLocationModalTitle')}
                >
                  {t('LocationSettings.addLocation')}
                </Button>

                {location.length > 0 && (
                  <div className="mt-2 overflow-auto pb-2" style={{ maxHeight: '150px' }}>
                    {location.map((loc, index) => (
                      <div
                        key={index}
                        className={`px-1 py-1 border mt-1 ${index === location.length - 1 ? 'mb-2' : 'mb-1'}`}
                        title={`${loc.number} - ${loc.title}`}
                      >
                        <div className="w-100 fs-15 d-flex justify-content-start align-items-center ">
                          <div className="d-flex justify-content-start flex-grow-1">
                            {truncateText(loc.number, 3)}
                            <span style={{ color: '#dee2e6', margin: '0 4px', fontWeight: 'bold' }}>–</span>
                            {truncateText(loc.title || t('LocationSettings.existingLocations'), 15)}
                          </div>

                          <div>
                            <button
                              type="button"
                              className="btn btn-sm btn-ghost-primary btn-icon"
                              onClick={() => editLocation(index)}
                              title={t('LocationSettings.edit')}
                            >
                              <i className="ri-pencil-fill icon icon__edit" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-ghost-primary btn-icon"
                              onClick={() => {
                                deleteLocation(index);
                              }}
                              title={t('LocationSettings.delete')}
                            >
                              <i className="ri-delete-bin-6-fill icon icon__delete" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddLocationPopup && (
        <div
          className="map-popup-content"
          style={{
            position: 'absolute',
            top: '900%',
            right: '-91px',
            width: '290px',
            height: '320px',
            overflow: 'hidden',
            padding: 0,
            zIndex: 9999,
            marginTop: '4px',
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          }}
        >
          <div className="py-2">
            <div className="border-bottom mb-3 w-100">
              <div className="d-flex justify-content-between align-items-center border-1 pb-1 px-2">
                <div style={{ fontWeight: 700, fontSize: 18, color: '#3a3a6a' }}>
                  {currentEditingIndex !== null
                    ? t('LocationSettings.editLocationModalTitle')
                    : t('LocationSettings.addLocationModalTitle')}
                </div>
                <button
                  className="btn btn-light waves-effect btn-sm fs-5 fw-semibold"
                  style={{ borderRadius: '50%', width: '32px', height: '32px', padding: 0 }}
                  onClick={() => {
                    if (currentEditingIndex !== null && tempPolygon.length === 0) {
                      enqueueSnackbar(t('LocationSettings.drawPolygonRequired') || 'Спочатку виділіть нову область!', {
                        variant: 'warning',
                      });
                      return;
                    }

                    setShowAddLocationPopup(false);
                    onToggleDrawMode(false);
                    setCurrentEditingIndex(null);
                  }}
                >
                  <i className="ri-close-line" />
                </button>
              </div>
            </div>

            <div className="px-2">
              <div className="row">
                <Controller
                  control={siteControl}
                  name="title"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput {...field} error={error?.message} label={t('LocationSettings.title')} />
                  )}
                />
                <div className="pt-2">
                  <Controller
                    control={siteControl}
                    name="number"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput {...field} error={error?.message} label={t('LocationSettings.number_of_devices')} />
                    )}
                  />
                </div>
                <div className="pt-2">
                  <label>{t('LocationSettings.color')}</label>
                  <input
                    type="color"
                    value={polygonColor}
                    onMouseDown={e => e.stopPropagation()}
                    onPointerDown={e => e.stopPropagation()}
                    onTouchStart={e => e.stopPropagation()}
                    onClick={() => {
                      if (tempPolygon.length === 0) {
                        onToggleDrawMode(false);

                        const resumeDrawing = () => {
                          onToggleDrawMode(true);
                          window.removeEventListener('mousemove', resumeDrawing);
                          window.removeEventListener('pointerdown', resumeDrawing, true);
                          window.removeEventListener('keydown', resumeDrawing);
                        };

                        setTimeout(() => {
                          window.addEventListener('mousemove', resumeDrawing);
                          window.addEventListener('pointerdown', resumeDrawing, true);
                          window.addEventListener('keydown', resumeDrawing);
                        }, 300);
                      }
                    }}
                    onChange={e => {
                      const newColor = e.target.value;

                      setPolygonColor(newColor);

                      if (colorDebounceRef.current) {
                        clearTimeout(colorDebounceRef.current);
                      }

                      colorDebounceRef.current = setTimeout(() => {
                        setColorPolygon(newColor);
                      }, 200);
                    }}
                    style={{
                      width: '100%',
                      height: '40px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      padding: '2px',
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="text-center pt-2">
              <Button
                className="btn btn-ghost-success"
                onClick={() => {
                  setValue('color', polygonColor);

                  let coordsToSubmit: [number, number][] = [];

                  if (tempPolygon.length > 0) {
                    coordsToSubmit = getStrictCoordinates();
                  } else if (currentEditingIndex !== null) {
                    coordsToSubmit = [];
                  }

                  setValue('coordinates', coordsToSubmit);

                  handleSubmit(onSubmitForm, onFormError)();
                }}
              >
                {t('LocationSettings.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSettings;
